import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';

const STORAGE_KEY = 'spforms_superadmin_tenant_override';

const TenantContext = createContext(null);

function readStoredTenantOverride() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? String(raw) : '';
  } catch {
    return '';
  }
}

function writeStoredTenantOverride(value) {
  if (!value) {
    localStorage.removeItem(STORAGE_KEY);
    return;
  }
  localStorage.setItem(STORAGE_KEY, String(value));
}

export function TenantProvider({ children }) {
  const { session } = useAuth();

  const role = session?.role || null;
  const userId = session?.user_id || null;
  const sessionTenantId = session?.tenant_id || null;
  const isSuperadmin = role === 'superadmin';

  const [tenantOverride, setTenantOverrideState] = useState('');

  useEffect(() => {
    if (isSuperadmin) {
      setTenantOverrideState(readStoredTenantOverride());
    } else {
      setTenantOverrideState('');
      writeStoredTenantOverride('');
    }
  }, [isSuperadmin]);

  const setTenantOverride = (tenantId) => {
    const nextValue = tenantId ? String(tenantId) : '';
    setTenantOverrideState(nextValue);
    writeStoredTenantOverride(nextValue);
  };

  const clearTenantOverride = () => {
    setTenantOverrideState('');
    writeStoredTenantOverride('');
  };

  const tenantId = isSuperadmin
    ? tenantOverride || null
    : sessionTenantId || null;

  const value = useMemo(
    () => ({
      tenantId,
      sessionTenantId,
      tenantOverride,
      setTenantOverride,
      clearTenantOverride,
      role,
      userId,
      isSuperadmin,
    }),
    [tenantId, sessionTenantId, tenantOverride, role, userId, isSuperadmin]
  );

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

export function useTenant() {
  const ctx = useContext(TenantContext);
  if (!ctx) {
    throw new Error('useTenant must be used inside TenantProvider');
  }
  return ctx;
}